import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { Checkbox } from '@/components/ui/checkbox'
import { donateSchema, type DonateSchema } from '@/lib/schemas'
import { useCreateDonation, useClub } from '@/lib/hooks'
import { useDonateStore } from '@/stores/donate'
import { cn, formatCurrency } from '@/lib/utils'

const PRESETS = [25, 50, 100, 250]

export function DonateForm() {
  const { data: club } = useClub()
  const { amount, frequency, setAmount, setFrequency } = useDonateStore()
  const submit = useCreateDonation()

  const form = useForm<DonateSchema>({
    resolver: zodResolver(donateSchema),
    mode: 'onBlur',
    defaultValues: { amount, frequency, anonymous: false, name: '', email: '' },
  })

  async function onSubmit(values: DonateSchema) {
    try {
      await submit.mutateAsync(values)
      toast.success(`Thank you for your ${values.frequency === 'monthly' ? 'monthly' : 'one-time'} donation of ${formatCurrency(values.amount)}!`, {
        description: 'A tax receipt will be emailed to you shortly.',
      })
      form.reset({ amount: 50, frequency: 'one-time', anonymous: false, name: '', email: '' })
      setAmount(50)
      setFrequency('one-time')
    } catch (err) {
      const e = err as { message?: string }
      toast.error(e.message ?? 'Donation failed. Please try again.')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
      <Field label="Frequency">
        <Controller
          control={form.control}
          name="frequency"
          render={({ field }) => (
            <div className="flex gap-2">
              {(['one-time', 'monthly'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => {
                    field.onChange(f)
                    setFrequency(f)
                  }}
                  className={cn(
                    'flex-1 rounded-md border px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors',
                    field.value === f
                      ? 'border-pitch-900 bg-pitch-900 text-white'
                      : 'border-border bg-card text-foreground hover:border-cosmos-green'
                  )}
                >
                  {f === 'one-time' ? 'One-Time' : 'Monthly'}
                </button>
              ))}
            </div>
          )}
        />
      </Field>

      <Field label="Amount">
        <div className="grid grid-cols-4 gap-2">
          {PRESETS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => {
                form.setValue('amount', v)
                setAmount(v)
              }}
              className={cn(
                'rounded-md border px-3 py-3 font-display text-xl transition-colors',
                form.watch('amount') === v
                  ? 'border-cosmos-green bg-cosmos-green text-white'
                  : 'border-border bg-card hover:border-cosmos-green hover:text-cosmos-green'
              )}
            >
              ${v}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Or custom amount (USD)" error={form.formState.errors.amount?.message}>
        <Input
          type="number"
          min={1}
          {...form.register('amount', {
            onChange: (e) => {
              const v = parseInt(e.target.value, 10)
              if (!isNaN(v)) setAmount(v)
            },
          })}
        />
      </Field>

      <Controller
        control={form.control}
        name="anonymous"
        render={({ field }) => (
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <Checkbox checked={field.value} onCheckedChange={(v) => field.onChange(v === true)} />
            Donate anonymously
          </label>
        )}
      />

      {!form.watch('anonymous') && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name (optional)"><Input {...form.register('name')} placeholder="Full name" /></Field>
          <Field label="Email (for receipt)" error={form.formState.errors.email?.message}>
            <Input type="email" {...form.register('email')} placeholder="you@example.com" />
          </Field>
        </div>
      )}

      <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Total</span>
        <span className="font-display text-3xl text-cosmos-green num">
          {formatCurrency(form.watch('amount') || 0)}
          <span className="text-xs text-muted-foreground uppercase tracking-widest ml-2 font-body">
            /{form.watch('frequency')}
          </span>
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-auto">We accept:</span>
        {['Visa', 'MC', 'PayPal', 'ACH'].map((m) => (
          <span key={m} className="rounded border border-border px-2 py-1 font-display text-xs text-muted-foreground tracking-wide">
            {m}
          </span>
        ))}
      </div>

      <Button type="submit" variant="gold" disabled={submit.isPending}>
        {submit.isPending ? 'Processing…' : (<><Heart className="h-4 w-4" /> Donate Now →</>)}
      </Button>

      {club && (
        <a
          href={`https://www.paypal.com/donate/?hosted_button_id=${club.payments.paypalDonateId}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-center text-muted-foreground hover:text-cosmos-green"
        >
          Or donate directly via PayPal →
        </a>
      )}
    </form>
  )
}
